import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Edit, Save, Trash2, Plus, Database } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminContentManager({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const navigate = useNavigate();
  const [allContent, setAllContent] = useState([]);
  const [editingContent, setEditingContent] = useState(null);
  const [newContent, setNewContent] = useState({ page: '', section: '', content: {} });
  const [contentJson, setContentJson] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchAllContent = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/content`, { headers: { Authorization: `Bearer ${token}` } });
      setAllContent(response.data);
    } catch (error) { toast.error('Failed to fetch content'); }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchAllContent();
  }, [user, navigate, fetchAllContent]);

  const handleSaveContent = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const contentData = { page: editingContent?.page || newContent.page, section: editingContent?.section || newContent.section, content: JSON.parse(contentJson) };
      await axios.post(`${API}/content`, contentData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Content saved');
      setEditingContent(null);
      setNewContent({ page: '', section: '', content: {} });
      setContentJson('');
      fetchAllContent();
    } catch (error) { toast.error('Failed to save: ' + (error.response?.data?.detail || error.message)); }
    finally { setLoading(false); }
  };

  const handleDeleteContent = async (page, section) => {
    if (!window.confirm('Delete this content?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/content/${page}/${section}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Content deleted');
      fetchAllContent();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const startEditing = (content) => {
    setEditingContent(content);
    setContentJson(JSON.stringify(content.content, null, 2));
  };

  const groupedContent = allContent.reduce((acc, item) => {
    if (!acc[item.page]) acc[item.page] = [];
    acc[item.page].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <span className="badge mb-3">ADMIN CONTROL</span>
            <h1 className="text-4xl font-bold text-foreground mb-2" data-testid="cms-title">Content Management</h1>
            <p className="text-muted-foreground">Edit all website content in real-time</p>
          </div>

          <Tabs defaultValue="edit" className="space-y-6">
            <TabsList className="bg-card border border-border/40">
              <TabsTrigger value="edit" data-testid="edit-tab"><Database className="w-4 h-4 mr-2" />Browse & Edit</TabsTrigger>
              <TabsTrigger value="create" data-testid="create-tab"><Plus className="w-4 h-4 mr-2" />Create New</TabsTrigger>
            </TabsList>

            <TabsContent value="edit">
              <div className="space-y-6">
                {Object.keys(groupedContent).length === 0 ? (
                  <Card className="bg-card border-border/40"><CardContent className="p-12 text-center"><Database className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No content found. Create your first content block.</p></CardContent></Card>
                ) : Object.entries(groupedContent).map(([page, contents]) => (
                  <motion.div key={page} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                    <h2 className="text-2xl font-bold text-primary font-mono-spec uppercase tracking-wider">{page} PAGE</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {contents.map(content => (
                        <Card key={content.id} className="bg-card border-border/40" data-testid={`content-card-${content.id}`}>
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg font-mono-spec text-foreground">{content.section}</CardTitle>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => startEditing(content)} className="border-border/40" data-testid={`edit-button-${content.id}`}><Edit className="w-3 h-3" /></Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDeleteContent(content.page, content.section)} data-testid={`delete-button-${content.id}`}><Trash2 className="w-3 h-3" /></Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <pre className="text-xs text-muted-foreground bg-background p-3 rounded overflow-x-auto max-h-40 border border-border/40">{JSON.stringify(content.content, null, 2)}</pre>
                            <p className="text-xs text-muted-foreground mt-2 font-mono-spec">Updated: {new Date(content.updated_at).toLocaleString()}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="create">
              <Card className="bg-card border-border/40">
                <CardHeader><CardTitle className="font-mono-spec text-primary">{editingContent ? 'EDIT CONTENT' : 'CREATE NEW CONTENT'}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {!editingContent && (
                    <>
                      <div><Label className="text-foreground font-mono-spec">Page</Label><Input value={newContent.page} onChange={(e) => setNewContent({...newContent, page: e.target.value})} placeholder="e.g., home, products" className="bg-background border-border/40 text-foreground" data-testid="page-input" /></div>
                      <div><Label className="text-foreground font-mono-spec">Section</Label><Input value={newContent.section} onChange={(e) => setNewContent({...newContent, section: e.target.value})} placeholder="e.g., hero, features" className="bg-background border-border/40 text-foreground" data-testid="section-input" /></div>
                    </>
                  )}
                  {editingContent && (
                    <div className="space-y-2">
                      <p className="text-muted-foreground font-mono-spec">Editing: <span className="text-primary">{editingContent.page}</span> / <span className="text-primary">{editingContent.section}</span></p>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingContent(null); setContentJson(''); }} className="text-muted-foreground hover:text-primary">Cancel Editing</Button>
                    </div>
                  )}
                  <div>
                    <Label className="text-foreground font-mono-spec">Content (JSON)</Label>
                    <Textarea value={contentJson} onChange={(e) => setContentJson(e.target.value)} rows={12} className="bg-background border-border/40 text-foreground font-mono-spec text-sm" placeholder={'{\n  "title": "Your Title"\n}'} data-testid="content-json-input" />
                  </div>
                  <Button onClick={handleSaveContent} disabled={loading || (!editingContent && (!newContent.page || !newContent.section)) || !contentJson} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-content-button">
                    <Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Content'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
